<template>
  <div class="setConfigurator">
    <v-stepper v-model="step" vertical :non-linear="true" class="configurationStepper">
      <v-stepper-step v-if="personSelectionNeeded"
                      :complete="step > personSelectionStep"
                      :step="personSelectionStep"
                      edit-icon="check"
                      editable
      >{{ $t("CONFIGURATOR_CHOOSE_PERSON") }}
        <small>{{ $t("CONFIGURATOR_CHOOSE_PERSON_HINT") }}</small>
      </v-stepper-step>
      <v-stepper-content v-if="personSelectionNeeded" :step="personSelectionStep">
        <PersonSelector v-model="personIndex" :persons="eligiblePersons" @select="step++"/>
      </v-stepper-content>

      <v-stepper-step v-if="configurationTypeSelectionNeeded"
                      :complete="step > configurationTypeSelectionStep"
                      :step="configurationTypeSelectionStep"
                      edit-icon="check"
                      editable
      >{{ $t("CONFIGURATOR_CHOOSE_MODE") }}
      </v-stepper-step>
      <v-stepper-content v-if="needsToChooseDetailedConfiguration" :step="configurationTypeSelectionStep">
        <ConfigurationModeSelector v-model="configurationMode"
                                   :product="product"
                                   ref="modeSelector"
                                   @select="nextStep"/>
      </v-stepper-content>

      <v-stepper-step
          :complete="step > imageSelectionStep"
          :step="imageSelectionStep"
          edit-icon="check"
      >{{ $t("CONFIGURATOR_CHOOSE_IMAGES") }}
      </v-stepper-step>
      <v-stepper-content :step="imageSelectionStep" class="imageStepperContent">
        <MultipleRequirementsSelector v-if="step === imageSelectionStep"
                                      v-model="imageSelection"
                                      class="multipleRequirementImageSelector"
                                      :requirements="imageRequirements"
                                      :person="person"
                                      :detailed-configuration="isDetailedConfiguration"
                                      ref="imageSelector"/>
      </v-stepper-content>
    </v-stepper>
    <v-row class="actionButtons" justify="center">
      <v-col v-if="addedToBasket" cols="auto">
        <v-btn class="actionButton" color="primary" @click="goToStep(3)">
          <v-icon>arrow_back</v-icon>
          {{ $t("BUTTON_BACK_TO_PRODUCTS") }}
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn v-if="configurationComplete" color="primary" class="actionButton" @click="addToBasket">
          <v-icon>add_shopping_cart</v-icon>
          {{ $t("BUTTON_ADD_TO_BASKET") }}
        </v-btn>
        <v-btn v-else-if="mayShowContinueButton" color="primary" class="actionButton" @click="continueImageSelection">
          {{ $t("BUTTON_CONTINUE") }}
        </v-btn>
      </v-col>
      <v-col v-if="addedToBasket" cols="auto">
        <v-btn class="actionButton" color="primary" @click="configureAgain">
          <v-icon>replay</v-icon>
          {{ $t("BUTTON_CONFIGURE_AGAIN") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {
  ConfiguredImageDto,
  DetailedImageRequirementDto,
  ImageRequirementDto,
  PersonDto,
  ProductDto,
  ProductMediumDto,
  ProductTypeDto
} from "@swagger";
import {Prop, Watch} from "vue-property-decorator";
import ImageSelector from "@/components/product-configuration/ImageSelector.vue";
import PersonSelector from "@/components/product-configuration/PersonSelector.vue";
import ConfigurationModeSelector from "@/components/product-configuration/ConfigurationModeSelector.vue";
import MultipleRequirementsSelector from "@/components/product-configuration/MultipleRequirementsSelector.vue";

@Component({
  components: {MultipleRequirementsSelector, ConfigurationModeSelector, PersonSelector, ImageSelector}
})
export default class SetConfigurator extends BasicComponent {

  @Prop(Object) product: ProductDto;

  step: number = 1;
  personIndex: number | null = null;
  configurationMode: string | null = null;
  selectingImages: boolean = false;
  autoSelection: ConfiguredImageDto[][] = []; // The imageSelection selected in automatic mode
  detailedSelection: ConfiguredImageDto[][] = []; // The imageSelection selected in detailed mode
  addedToBasket: boolean = false;

  created() {
    this.resetImageSelection();
  }

  mounted() {
    if (this.step === this.imageSelectionStep) {
      this.selectingImages = true;
    }
  }

  get imageSelection(): ConfiguredImageDto[][] {
    return this.isDetailedConfiguration ? this.detailedSelection : this.autoSelection;
  }

  set imageSelection(value: ConfiguredImageDto[][]) {
    if (this.isDetailedConfiguration) this.detailedSelection = value;
    else this.autoSelection = value;
  }

  get eligiblePersons(): PersonDto[] {
    return this.allPersons.filter((person: PersonDto) => {
      const images = this.getPersonImages(person);
      return person.productIds.includes(this.product.id)
          && this.imageRequirements.every(r => images.some(image => this.imageMatchesRequirement(image, r.requirement)));
    });
  }

  nextStep() {
    this.step++;
  }

  /**
   * @return A human-readable description of the requirement
   */
  getAutoRequirementLabel(requirement: ImageRequirementDto): string {
    return requirement.types.map(t => this.$t("IMAGE_NAME_" + t)).join(" / ");
  }

  /**
   * Tells the image selector to go to the next step
   */
  continueImageSelection(): void {
    this.getImageSelector().navigateToNextStep();
  }

  addToBasket(): void {
    this.addArticleToBasket(this.product.id, this.flattenArray(this.imageSelection), this.isDetailedConfiguration, () => {
      this.$emit("addedToBasket");
      this.addedToBasket = true;
    }, (error) => {
      this.$emit("error", error);
    });
  }

  configureAgain(): void {
    this.addedToBasket = false;
    this.step = 1;
    this.personIndex = null;
    this.configurationMode = null;
    this.resetImageSelection();
    const imageSelector = this.getImageSelector();
    if (imageSelector) {
      imageSelector.reset();
    }
  }

  get imageRequirements(): DetailedImageRequirementDto[] {
    if (this.isDetailedConfiguration) return this.product.detailedImageRequirements;
    return this.product.imageRequirements.map(r => {
      return {
        requirement: r,
        productName: this.getAutoRequirementLabel(r)
      };
    })
  }

  get isDetailedConfiguration() {
    if (this.detailedConfigurationObsolete) return false;
    return !this.autoConfigurationPossible || this.configurationMode === "DETAILED";
  }

  get detailedConfigurationObsolete() {
    return this.product.imageRequirements.length === this.product.detailedImageRequirements.length;
  }

  get autoConfigurationPossible() {
    const requirements = this.product.imageRequirements;
    const condition = (i: ImageRequirementDto) => i.type === ImageRequirementDto.TypeEnum.ALLOFPERSON;
    if (requirements.some(condition) && !requirements.every(condition)) return false;
    const detailedRequirements = this.product.detailedImageRequirements;
    if (detailedRequirements.filter(r => r.productMedium === ProductMediumDto.DOWNLOAD).length > 1) return false;
    return true;
  }

  get needsToChooseDetailedConfiguration() {
    if (this.product.productType !== ProductTypeDto.SET) return false;
    if (this.detailedConfigurationObsolete || !this.autoConfigurationPossible) return false;
    return true;
  }

  get personSelectionNeeded() {
    const persons = this.eligiblePersons;
    // TODO Automatic person selection for friend image products
    return persons && persons.length > 1;
  }

  get personSelectionStep() {
    return this.personSelectionNeeded ? 1 : 0;
  }

  get configurationTypeSelectionNeeded() {
    return this.needsToChooseDetailedConfiguration;
  }

  get configurationTypeSelectionStep() {
    return this.personSelectionStep + (this.configurationTypeSelectionNeeded ? 1 : 0);
  }

  get imageSelectionStep() {
    return this.configurationTypeSelectionStep + 1;
  }

  get person(): PersonDto {
    return this.eligiblePersons[this.personIndex ? this.personIndex : 0];
  }

  getImageSelector(): MultipleRequirementsSelector {
    return this.$refs["imageSelector"] as MultipleRequirementsSelector;
  }

  get configurationComplete(): boolean {
    return this.selectingImages && this.getImageSelector().configurationComplete;
  }

  get mayShowContinueButton(): boolean {
    return this.selectingImages && this.getImageSelector().currentRequirementSatisfied;
  }

  @Watch("product")
  onProductChange(newProduct: ProductDto, oldProduct: ProductDto): void {
    this.resetImageSelection();
  }

  @Watch("personIndex")
  onPersonChange(newIndex: number, oldIndex: number): void {
    this.resetImageSelection();
  }

  resetImageSelection(): void {
    this.autoSelection = this.product.imageRequirements.map(_ => []);
    this.detailedSelection = this.product.detailedImageRequirements.map(_ => []);
  }

  @Watch("step")
  onStepChange(step, oldVal) {
    if (step === this.imageSelectionStep) {
      this.$nextTick(() => this.selectingImages = true);
    } else {
      this.selectingImages = false;
    }
    if (step === this.configurationTypeSelectionStep) {
      this.configurationMode = null;
    }
  }

}

</script>
<style lang="scss" scoped>
  .setConfigurator {
    max-height: 100%;
    display: flex;

    .configurationStepper {
      max-height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .imageStepperContent {
        display: flex;
        max-height: 100%;
        flex-direction: column;
        flex: 0 1 auto;
        overflow-x: hidden;
        overflow-y: scroll;
      }
    }
  }
</style>
<style lang="scss">
.configurationStepper {
  .v-stepper__content {
    &:last-of-type {
      margin-left: 8px;
    }
  }
}
</style>